/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CareerSiteBrandConfigurationV2Response } from '../models/CareerSiteBrandConfigurationV2Response';
import type { CareerSiteLocationResponse } from '../models/CareerSiteLocationResponse';
import type { CareerSiteOpeningV2Response } from '../models/CareerSiteOpeningV2Response';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class CareerSiteV2Service {

    /**
     * brand configuration with interdependent filters
     * Returns brand configuration including filter options for interdependent filtering. This endpoint provides all necessary data for the career site v2 including job types, hours, locations (countries/states/cities), categories, and positions.
     * @param careerSite Career Site Params
     * @returns CareerSiteBrandConfigurationV2Response successful
     * @throws ApiError
     */
    public static getInternalApiCareerSiteV2BrandsConfiguration(
        careerSite: {
            account_slug: string;
            brand_id?: string;
            locale?: string;
            country?: string;
            state?: string;
        },
    ): CancelablePromise<CareerSiteBrandConfigurationV2Response> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/career_site/v2/brands/configuration',
            query: {
                'career_site': careerSite,
            },
            errors: {
                404: `not found when feature flag is disabled`,
            },
        });
    }

    /**
     * list locations with interdependent filters
     * Returns a list of locations with active funnels. This endpoint supports filtering by location name and pagination. Requires the interdependent filters feature flag to be enabled.
     * @param careerSite Career Site Params
     * @param query Search locations by name (case-insensitive)
     * @param page Page number for pagination
     * @param perPage Number of items per page
     * @param skipPagination Skip pagination
     * @returns CareerSiteLocationResponse successful
     * @throws ApiError
     */
    public static getInternalApiCareerSiteV2Locations(
        careerSite: {
            account_slug: string;
            brand_id?: string;
        },
        query?: string,
        page?: number,
        perPage?: number,
        skipPagination?: boolean,
    ): CancelablePromise<CareerSiteLocationResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/career_site/v2/locations',
            query: {
                'career_site': careerSite,
                'query': query,
                'page': page,
                'per_page': perPage,
                'skip_pagination': skipPagination,
            },
            errors: {
                404: `not found when feature flag is disabled`,
            },
        });
    }

    /**
     * list openings with interdependent filters
     * Returns a list of openings with active funnels. This endpoint supports interdependent filtering with array support for multiple filter values. Requires the interdependent filters feature flag to be enabled.
     * @param careerSite Career Site Params
     * @param page Page number for pagination
     * @param radius Distance Radius
     * @param sortBy Sorting param
     * @param query Search string
     * @param location Location filter (all, current_location, remote, or location external_id). Defaults to all when omitted.
     * @param locale Target locale for translation
     * @param category Category filter (array)
     * @param position Position filter (array)
     * @param jobType Job type filter (array)
     * @param jobHours Job hours filter (array)
     * @param country Country filter (array)
     * @param state State filter (array)
     * @param city City filter (array)
     * @param locationName Location name filter (array)
     * @param countsOnly When true, returns only filter_counts without openings (for mobile filter drawer)
     * @returns CareerSiteOpeningV2Response successful with counts_only=true returns empty openings and filter_counts
     * @throws ApiError
     */
    public static getInternalApiCareerSiteV2Openings(
        careerSite: {
            account_slug: string;
            brand_id?: string;
            position_id?: string;
            location_id?: string;
            experience_level?: string;
            place_id?: string;
            is_jobs_from_current_location?: boolean;
        },
        page?: number,
        radius?: string,
        sortBy?: 'distance' | 'title' | 'date_posted',
        query?: string,
        location?: string,
        locale?: 'af-ZA' | 'sq-AL' | 'am-ET' | 'ar' | 'hy-AM' | 'az-AZ' | 'bn-IN' | 'bs-BA' | 'bg-BG' | 'ca' | 'zh-CN' | 'zh-HK' | 'zh-TW' | 'hr-HR' | 'cs-CZ' | 'da-DK' | 'fa-AF' | 'nl-NL' | 'en-GB' | 'en-US' | 'et-EE' | 'fa-IR' | 'fi-FI' | 'fr-FR' | 'fr-CA' | 'ka-GE' | 'de-DE' | 'el-GR' | 'gu-IN' | 'ht-HT' | 'ha' | 'he-IL' | 'hi-IN' | 'hu-HU' | 'id-ID' | 'it-IT' | 'ja-JP' | 'kn-IN' | 'kk-KZ' | 'ko-KR' | 'lv-LV' | 'lt-LT' | 'mk-MK' | 'ms-MY' | 'ml-IN' | 'mr-IN' | 'mn-MN' | 'no-NO' | 'ps' | 'pl-PL' | 'pt-BR' | 'pt-PT' | 'pa-IN' | 'ro-RO' | 'ru-RU' | 'sr-RS' | 'si-LK' | 'sk-SK' | 'sl-SI' | 'so' | 'es-ES' | 'es-MX' | 'sw' | 'sv-SE' | 'tl-PH' | 'ta-IN' | 'te-IN' | 'th-TH' | 'tr-TR' | 'uk-UA' | 'ur' | 'uz-UZ' | 'vi-VN',
        category?: Array<string>,
        position?: Array<string>,
        jobType?: Array<string>,
        jobHours?: Array<string>,
        country?: Array<string>,
        state?: Array<string>,
        city?: Array<string>,
        locationName?: Array<string>,
        countsOnly?: boolean,
    ): CancelablePromise<CareerSiteOpeningV2Response> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/career_site/v2/openings',
            query: {
                'career_site': careerSite,
                'page': page,
                'radius': radius,
                'sort_by': sortBy,
                'query': query,
                'location': location,
                'locale': locale,
                'category[]': category,
                'position[]': position,
                'job_type[]': jobType,
                'job_hours[]': jobHours,
                'country[]': country,
                'state[]': state,
                'city[]': city,
                'locationName[]': locationName,
                'counts_only': countsOnly,
            },
            errors: {
                404: `not found when feature flag is disabled`,
            },
        });
    }

}